// Org-wide activity timeline. Filters by category (All / Leads / Calls / Notes)
// which maps to a set of Activity.type enum values on the backend.

import React, { useState } from "react";
import { Loader2, AlertCircle, RefreshCw, ChevronLeft, ChevronRight, X } from "lucide-react";
import { DatePicker, Select as AntSelect } from "antd";
import dayjs from "dayjs";
import { useActivities } from "../hooks/useActivities";
import { useTeamMembers, formatMember, isIdentifiableMember } from "../hooks/useTeamMembers";
import ActivityRow from "../molecules/ActivityRow";
import PageHeader from "../organisms/PageHeader";
import TableFooter from "../organisms/TableFooter";
import { Button } from "../atoms";

const { RangePicker } = DatePicker;

const PAGE_SIZE = 20;

const FILTERS = [
  { id: "all",    label: "All",    types: undefined },
  // LEAD_STATUS_CHANGED is what the services actually emit for non-won/lost
  // status moves; STATUS_CHANGED is the older name and stays for legacy rows.
  { id: "leads",  label: "Leads",  types: ["LEAD_CREATED", "LEAD_UPDATED", "LEAD_DELETED", "LEAD_WON", "LEAD_LOST", "LEAD_REOPENED", "STATUS_CHANGED", "LEAD_STATUS_CHANGED", "PIPELINE_MOVE"] },
  { id: "calls",  label: "Calls",  types: ["CALL_INITIATED", "CALL_COMPLETED", "CALL_FAILED", "CALL_NOTE_ADDED", "CALL_DELETED"] },
  { id: "notes",  label: "Notes",  types: ["NOTE_ADDED", "NOTE_DELETED"] },
  { id: "contacts", label: "Contacts", types: ["CONTACT_ADDED", "CONTACT_UPDATED", "CONTACT_DELETED"] },
  { id: "followups", label: "Follow-ups", types: ["FOLLOWUP_SCHEDULED", "FOLLOWUP_COMPLETED", "FOLLOWUP_CLEARED", "FOLLOWUP_SNOOZED"] },
];

function formatErr(err) {
  const data = err?.response?.data;
  if (typeof data === "string") return data;
  if (data?.error) return typeof data.error === "string" ? data.error : data.error.message;
  return err?.message || "Request failed";
}

export default function Activities() {
  const [filterId, setFilterId] = useState("all");
  const [page, setPage] = useState(0);
  const [userId, setUserId] = useState("");
  const [from, setFrom] = useState("");
  const [to, setTo] = useState("");
  const filter = FILTERS.find((f) => f.id === filterId) || FILTERS[0];
  const { members } = useTeamMembers();
  const { data, isLoading, isError, error, refetch, isFetching } = useActivities({
    types: filter.types,
    userId: userId || undefined,
    from: from || undefined,
    to: to || undefined,
    limit: PAGE_SIZE,
    skip: page * PAGE_SIZE,
  });
  const items = data?.items || [];
  const total = data?.total ?? 0;
  const pageCount = Math.max(1, Math.ceil(total / PAGE_SIZE));
  const onChangeFilter = (id) => { setFilterId(id); setPage(0); };
  const onChangeUser = (v) => { setUserId(v); setPage(0); };
  const onChangeRange = (dates) => {
    const f = dates?.[0] ? dates[0].format("YYYY-MM-DD") : "";
    const t = dates?.[1] ? dates[1].format("YYYY-MM-DD") : "";
    setFrom(f); setTo(t); setPage(0);
  };
  const hasExtraFilters = !!(userId || from || to);
  const clearExtraFilters = () => { setUserId(""); setFrom(""); setTo(""); setPage(0); };
  const rangeValue = from || to ? [from ? dayjs(from) : null, to ? dayjs(to) : null] : null;

  return (
    <div style={{ display: "flex", flexDirection: "column", height: "100%" }}>
      <PageHeader
        title="Recent activity"
        actions={
          <Button variant="secondary" size="sm" leftIcon={<RefreshCw size={14} />} onClick={() => refetch()}>
            {isFetching ? "Refreshing…" : "Refresh"}
          </Button>
        }
        topRight={<>
          {FILTERS.map((f) => (
            <button
              key={f.id}
              onClick={() => onChangeFilter(f.id)}
              className="tg-tool"
              style={f.id === filterId ? { background: "var(--color-hover-bg)", color: "var(--color-text-primary)", fontWeight: 500 } : undefined}
            >
              {f.label}
            </button>
          ))}
          <span className="tg-tool-sep" />
          <AntSelect
            value={userId || undefined}
            onChange={(v) => onChangeUser(v || "")}
            placeholder="All members"
            allowClear
            showSearch
            optionFilterProp="label"
            style={{ minWidth: 180, height: 28 }}
            options={members.filter(isIdentifiableMember).map((m) => ({ value: m._id, label: formatMember(m) }))}
            aria-label="Filter by member"
          />
          <RangePicker
            value={rangeValue}
            onChange={onChangeRange}
            format="DD MMM YYYY"
            placeholder={["From", "To"]}
            style={{ height: 28 }}
            allowEmpty={[true, true]}
          />
          {hasExtraFilters && (
            <button onClick={clearExtraFilters} className="tg-tool" aria-label="Clear filters">
              <X size={13} /> Clear
            </button>
          )}
        </>}
      />

      <div style={{ flex: 1, padding: "16px 24px", overflowY: "auto" }}>
        {isLoading && (
          <div style={{ display: "flex", alignItems: "center", gap: 10, padding: 40, justifyContent: "center", color: "var(--color-text-secondary)" }}>
            <Loader2 size={18} className="animate-spin" /> Loading activities…
          </div>
        )}
        {isError && (
          <div style={{ background: "var(--crm-danger-soft)", color: "var(--crm-danger)", padding: 16, borderRadius: 8, display: "flex", alignItems: "flex-start", gap: 12 }}>
            <AlertCircle size={18} style={{ flexShrink: 0, marginTop: 2 }} />
            <div style={{ flex: 1 }}>
              <div style={{ fontWeight: 600, marginBottom: 4 }}>Couldn't load activities</div>
              <div style={{ fontSize: 13, opacity: 0.85 }}>{formatErr(error)}</div>
            </div>
          </div>
        )}
        {!isLoading && !isError && items.length === 0 && (
          <div style={{ padding: 40, textAlign: "center", color: "var(--color-text-secondary)" }}>
            <div style={{ fontSize: 15, marginBottom: 6 }}>No activity yet for this filter.</div>
            <div style={{ fontSize: 13 }}>Create or update a lead, log a call, or add a note to see it here.</div>
          </div>
        )}
        {!isLoading && !isError && items.length > 0 && (
          <div style={{ background: "var(--color-bg-surface)", border: "1px solid var(--color-border-subtle)", borderRadius: 8, overflow: "hidden" }}>
            {items.map((a) => (
              <ActivityRow key={a._id} activity={a} />
            ))}
            <TableFooter
              page={page + 1} pages={pageCount} total={total}
              pageSize={PAGE_SIZE} shown={items.length} label="activities"
              onChange={(p) => setPage(p - 1)}
            />
          </div>
        )}
      </div>
    </div>
  );
}
